const translation = {
  // Navigation
  nav: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    pricing: 'Pricing',
    bookConsultation: 'Book Consultation',
    testimonials: 'Testimonials',
    faq: 'FAQ',
    policyUpdates: 'Policy Updates',
    // [T243] The ground truth's SECOND Resources row (site/index.html
    // §site-header). It was deliberately absent until now because `/guides` had
    // no index route and a nav entry pointing at a 404 is worse than a missing
    // one (Header.tsx's T234 note). This card ships that route, so the row lands
    // with it — the two changes belong in one commit or neither.
    guides: 'Guides',
    pathwayFinder: 'Pathway Finder',
    // [T336] the Resources row for the live CRS calculator (/crs-calculator).
    crsCalculator: 'CRS calculator',
    contact: 'Contact',
    // [T234] The dropdown this key labels is the ground truth's "Resources"
    // group, not the old "More" overflow. The KEY NAME is deliberately NOT
    // renamed: `src/pages/t95-public-trust.test.tsx` (CHROME_KEYS) pins
    // `nav.more` as a string the header must render, and that lock sits outside
    // this card's write-face — a rename would turn it red for a rename's sake.
    more: 'Resources',
    methodology: 'Methodology',
    login: 'Login',
    // [T234] The Services dropdown — the ground truth's six service pages
    // (docs/design/rebuild-v0b7764f/site/index.html §site-header). Every entry
    // resolves to an EXISTING route: `/services/<slug>` is a real page for all
    // six slugs in src/data/programsData.json, so none of these promises a page
    // that does not exist yet.
    servicesMenu: {
      expressEntry: 'Express Entry — STEM & CEC',
      bcPnpTech: 'BC PNP Tech',
      spousalSponsorship: 'Spousal & Family Sponsorship',
      c11Entrepreneur: 'C11 Entrepreneur Work Permit',
      superVisa: 'Parents & Grandparents Super Visa',
      pgwp: 'Study Permits & PGWP',
    },
  },

  // Header
  header: {
    bookConsultation: 'Book Consultation',
    openMenu: 'Open main menu',
  },

  // Shared micro-copy
  common: {
    close: 'Close',
  },

  // 404 (rendered outside LangLayout — language comes from the URL prefix)
  notFound: {
    title: 'Page not found',
    body: 'The page you are looking for does not exist or has moved.',
    home: 'Back to home',
  },

  // Pathway Finder (interactive tool) Page
  pathwayFinder: {
    badge: 'Free · ~8–10 min · no sign-up',
    title: 'Free Canadian immigration assessment',
    subtitle:
      'Answer questions about your goal, education, language and work — we’ll show the pathways that fit, an indicative Express Entry score, and connect you with our licensed RCIC.',
    intro:
      'This comprehensive self-assessment mirrors the factors IRCC actually scores. It gives you a directional read — candidate pathways and an indicative CRS estimate — not an eligibility decision. Most people finish in 8–10 minutes; spouse and language sections only appear if they apply to you.',
    start: 'Start the assessment',
    disclaimer:
      'General information only — not legal advice or an assessment of your eligibility. No consultant-client relationship is formed by using this tool.',
    progress: 'Step {{n}} of {{total}}',
    choose: 'Select…',
    continue: 'Continue',
    back: 'Back',
    ability: { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' },
    yourClb: 'Your controlling level: CLB {{clb}}',
    source: 'Source',
    countryPlaceholder: 'Start typing your country…',
    clbTitle: 'What’s a CLB? (Canadian Language Benchmark)',
    clbHelp: 'IRCC converts your test sub-scores to a CLB level per ability (NCLC for French). Program eligibility uses your lowest ability. Below: the minimum score for each CLB.',
    clbSource: 'Official IRCC equivalency charts',
    nocFind: 'Not sure? Find your NOC / TEER',
    nocTitle: 'Find your NOC / TEER',
    nocIntro: 'Your occupation’s TEER level decides Express Entry eligibility — TEER 0–3 generally count for the Federal Skilled Worker and Canadian Experience Class. Pick the tier that matches your main duties.',
    nocEligible: 'Counts for Express Entry',
    nocNotEligible: 'Generally not eligible',
    nocExamples: 'Examples',
    nocDisclaimer: 'Examples are orientation only — confirm your NOC by matching the official duties. Book a consultation for a case-specific determination.',
    nocOfficial: 'Open the official NOC finder',
    resultsDisclaimer:
      'These are general candidate pathways and an indicative estimate based only on what you entered — not an eligibility determination, legal advice, or a guarantee of any outcome. A consultation confirms what’s realistic for your case.',
    resultsTitle: 'Pathways worth exploring',
    officialSource: 'Official source',
    crsTitle: 'Indicative Express Entry (CRS) estimate',
    crsLabel: {
      competitive: 'Competitive for recent draws (the range varies by draw type).',
      borderline: 'Borderline — a provincial nomination or category draw may be your best route.',
      improve: 'Below recent general cut-offs — a nomination, more language/experience, or a category draw can help.',
    },
    crsWithSpouse: ' (scored with your spouse/partner).',
    assumesECA: 'Assumes a qualifying Educational Credential Assessment (ECA) for your foreign education.',
    assumesLang: 'Assumes a qualifying language-test result.',
    crsDisclaimer:
      'Indicative estimate only, calculated from what you entered using IRCC’s published CRS criteria. Not a guarantee of your actual score or of an Invitation to Apply. Your official score is set only when you submit an Express Entry profile to IRCC, and cut-offs change every draw.',
    ctaTitle: 'Get your personalized read from a licensed RCIC',
    ctaSubtitle:
      'Send your assessment to our licensed RCIC, Yansi He (R708210), and book a 45-minute consultation (CA$99, credited toward your service fee if you retain us) to confirm your best pathway.',
    bookCta: 'Book a consultation',
    nameLabel: 'Your name',
    emailLabel: 'Your email',
    phoneLabel: 'Phone / WeChat (optional)',
    consent:
      'I consent to Think Forward Immigration collecting the information in this assessment and emailing it to its licensed RCIC to prepare for and contact me about a consultation. I understand this is a general, indicative result only — not immigration advice or an eligibility decision — and that submitting it does not create a consultant-client relationship. I can withdraw consent or request deletion by emailing info@thinkforwardic.com.',
    emailMe: 'Also email me a copy of my results.',
    leadSubmit: 'Send my assessment',
    leadSuccess: 'Thanks! Your assessment is on its way to our RCIC and we’ll be in touch. Check your inbox — and book a consultation any time.',
    leadError: 'Please add your name, email, and tick the consent box (or email info@thinkforwardic.com).',
    restart: 'Start over',
  },

  // Policy Updates (timeline) Page
  policyUpdates: {
    hero: {
      badge: 'Updated June 2026',
      title: 'Canadian Immigration Policy Updates',
      subtitle:
        'We track every major IRCC change — what happened, when, and what it means for you — each linked to its official source. The rules keep moving; staying current is our job.',
      lede:
        'Canada’s immigration rules are changing fast in 2025–2026. We track every major IRCC change — what happened, when it took effect, and what it means — each linked to its official government source.',
    },
    lastUpdated: 'Last updated',
    faqHeading: 'Common questions about Canada’s 2026 immigration changes',
    faq: {
      q1: 'What are the biggest Canadian immigration changes in 2026?',
      a1: 'Key 2026 changes include lower permanent-resident targets, a study-permit cap (with master’s and PhD students at public universities now exempt from the PAL), Express Entry category draws shifting toward medical, research and transport occupations, and tighter spouse open work permit and low-wage LMIA rules. Each change above links to its official IRCC source.',
      q2: 'Can a spouse still get an open work permit in Canada?',
      a2: 'Since January 21, 2025, a spousal open work permit is generally limited to spouses of workers in higher-skilled (TEER 0 or 1) occupations or select priority sectors, where the principal worker has enough time left on their permit. Spouses of most students and lower-skilled workers no longer qualify.',
      q3: 'Does a job offer still add CRS points in Express Entry?',
      a3: 'No. As of March 25, 2025, a valid job offer — including LMIA-backed offers — no longer adds CRS points in Express Entry (it previously added 50 or 200). A job offer can still matter for some program eligibility.',
      q4: 'Do master’s and PhD students still need a Provincial Attestation Letter (PAL)?',
      a4: 'From January 1, 2026, students starting a master’s or doctoral program at a public designated learning institution are exempt from the PAL requirement on their study permit application.',
      q5: 'How many hours can international students work off campus?',
      a5: 'Eligible students can work up to 24 hours per week off campus during academic sessions (raised from 20 in late 2024), and full-time during scheduled breaks.',
    },
    filter: {
      all: 'All updates',
      current: 'In force now',
      past: 'Past / updated',
    },
    status: {
      current: 'In force',
      superseded: 'Superseded',
      historical: 'Historical',
    },
    criteria: 'What we include: major Canadian immigration changes since 2023 that have a real impact on applicants — each linked to its official IRCC / government source.',
    legend: {
      current: 'the rule still applies today',
      superseded: 'replaced by a newer policy below',
      historical: 'a one-time event or an ended pathway — no longer a current rule',
    },
    label: {
      source: 'Official source',
      whatThisMeans: 'What this means · how we help',
    },
    share: {
      label: 'Share',
      wechat: 'WeChat',
      email: 'Email',
      copy: 'Copy link',
      copied: 'Link copied',
      wechatHint: 'Link copied — paste it into WeChat to share',
    },
    disclaimer:
      'Information current as of June 2026 and provided for general guidance only — immigration rules change frequently. Always confirm on the official IRCC website (canada.ca), and book a consultation for advice on your specific case.',
    cta: {
      title: 'Not sure how the latest rules affect your case?',
      subtitle:
        'Book a 45-minute consultation (CA$99, credited toward your service fee if you retain us) with licensed RCIC Yansi He and get a clear, current read on your options.',
      button: 'Book a consultation',
    },
  },

  // Home Page
  // [T234] The hero headline + subtitle are the ground truth's
  // (docs/design/rebuild-v0b7764f/site/index.html §HERO), adopted by owner
  // ruling 2026-07-29. "Achieve Your Canadian Dream Today" was industry-wide
  // boilerplate that says nothing only we could say; this pair names the actual
  // problem (policy churn), the actual remedy (a free 10-minute assessment) and
  // the actual authority behind it (a licensed RCIC). Loss framing, zero
  // outcome promise — the compliance linter reads this file (T216).
  'home.hero.title': 'Immigration policy keeps changing. Know where you stand.',
  'home.hero.subtitle': 'A free 10-minute assessment maps your options — built and reviewed by a licensed RCIC in Vancouver.',
  // Left as-is deliberately: the hero's button renders `pathwayFinder.start`
  // (the shared free-assessment label), and `src/pages/t95-public-trust.test.tsx`
  // requires THIS key's value to appear on the page — it does, via
  // `home.cta.button`. Re-pointing or re-wording it is a separate card; see
  // T234 §執行器裁決記 ①.
  'home.hero.cta': 'Start your free assessment',

  // T95 credibility strip (hero): factual license/verification/language facts only
  'home.credibility.rcic': 'Yansi He, Licensed RCIC R708210',
  'home.credibility.cicc': 'Verify on the CICC public register',
  // [T234] Shortened to the ground truth's own wording. Same fact, 79px less of
  // it: measured at 1440 the five-fact strip came to 1143px against 1120px of
  // row, so the location slot wrapped onto a second line and the strip read as
  // 4+1 instead of the single hairline row the face is copied from. "Services
  // in" was the only word-pair on the strip carrying no information — every
  // other slot is already at its shortest truthful form. (zh is untouched:
  // 中英双语服务 is 104px and its row never overflowed.)
  'home.credibility.bilingual': 'English · 中文',

  // [T234] Trust strip — the two facts T216/T223 refused to write for want of a
  // source (coordination log 547) now have one: the CICC public register,
  // transcribed field-by-field in docs/research/register-facts-2026-07-29.md.
  // Wording ceiling is set there and MUST NOT be widened: the register publishes
  // outcomes on a date, so "zero disciplinary record" is sayable and any
  // "clean-record guarantee" reading is not.
  'home.trust.licensedSince': 'RCIC licensed since 2021',
  'home.trust.noDiscipline': 'Zero disciplinary record',
  // B2-approved commitment — [T275 · Q5] re-anchored to the retainer agreement
  // (owner ruling §5-R Q5: the price lives in the contract, not in a separate
  // quote document nobody produces). Chip slot, so the timing invariant
  // ("before any further payment") is carried by the full-sentence sites this
  // chip summarizes (/pricing COMMITMENTS[1] and the /testimonials cell body
  // that renders under this very string).
  'home.trust.fixedFee': 'Fee fixed in writing in your signed retainer agreement',

  // [T234] The policy strip's lead-in, per the ground truth's `policy-label`.
  // It labels WHO is being watched, which "Policy Updates" (a nav destination)
  // never did.
  'home.policyWatch.label': 'IRCC policy watch:',

  // ── [T244] WHY US — the ground truth's copy, in place of the Readdy-era four ──
  //
  // T240 gave this section the ground truth's FORM (four hairline columns, no
  // card, no resting shadow) and said so in its own header: "ZERO COPY CHANGES,
  // still." This card is the copy half, and it is the difference between four
  // slogans and four checkable statements:
  //
  //   was → "Why Choose Us" · "Your goals and needs are at the heart of
  //          everything we do" · "Professional, regulated immigration consulting
  //          services" — sentences any firm on earth could publish unaltered.
  //   is  → a heading that names the one thing a reader can act on ("you can
  //          verify"), and four cells that each carry a fact or a boundary: one
  //          consultant end to end · a written quote before payment · the
  //          register entry with its dates · and we say no when the case is weak.
  //
  // TRANSCRIPTION, NOT AUTHORSHIP (exceptions named below): every string is a
  // verbatim text node of `docs/design/rebuild-v0b7764f/site/index.html` §WHY US
  // (zh → `site/zh/index.html`), and `homeFace.test.tsx` §k re-reads those files
  // on every run as EXACT membership — a tidy-up here stops matching its source.
  //
  // [T275 · Q5+Q7] TWO MORE RULED DIVERGENCES (owner, claims-audit §5-R,
  // 2026-08-02), pinned in homeFace §k as DIVERGENCE 3 instead of the verbatim
  // set: `client.desc` no longer locks a headcount ("One licensed consultant"
  // → a licensed consultant, personally responsible — Q7: the claim would turn
  // false the day a second consultant or an assistant touches a file), and
  // `transparent.desc` re-anchors the fee promise to the signed retainer
  // agreement (Q5: the "written fixed quote" document does not exist; the
  // contract does). The reference zip keeps the OLD sentences; the ruling
  // outranks the transcription for exactly these two cells.
  //
  // [T280 · Q6-A] THE HONEST CELL'S FIRST SENTENCE IS NOW RULED TOO (owner,
  // claims-audit §5-R 增裁, 2026-08-02): the loss-framing transcription ("We
  // tell you if your case is NOT worth filing — before you spend money") is
  // upgraded to the psychology-A form the owner picked — the same refusal
  // duty, plus the constructive half ("tell you exactly what would make it
  // ready"). The zip keeps the OLD cell; homeFace §k DIVERGENCE 1 is
  // re-recorded as a byte-pin of the ruled pair (+ the unchanged restated
  // second sentence), with the zip form asserted to stay out.
  //
  // `stress` → `honest` is a KEY RETIREMENT, not a rename in place: the ground
  // truth's fourth cell is "Honest Screening", and a key called `stress` holding
  // it leaves the next reader hunting for stress-free guidance that no longer
  // exists (T240's precedent — a retired key left in the file is a revert waiting
  // for a merge conflict; `pilot.test.tsx` asserts both languages dropped it).
  //
  // ⛔ THE FOURTH CELL'S SECOND SENTENCE — a recorded, deliberate divergence.
  // The ground truth ends it "No consultant can legally guarantee approval."
  // That sentence trips `en-guarantee-outcome` (`guarantee … approval`) because
  // the pattern registry has no negation awareness — the blind spot CV-35
  // records for the pathway finder's own disclaimer — and its zh twin trips
  // `zh-baozheng-outcome` on 保证获批 from the other side. CV-35's repair was an
  // ALLOWLIST entry; this card cannot take that route (`src/i18n/lint-allowlist.json`
  // and `COMPLIANCE-VERIFY.md` are both outside its write-face, and an exemption
  // with no verification row against it is the blindfold the repo forbids). So the
  // clause is RESTATED from the decision-maker's side, in the words this site
  // already publishes for exactly this duty (`src/pages/services/copy.ts`
  // §disclaimer, T236) — same duty, no linter exemption, one voice. `homeFace` §k
  // pins it against that file so the two can never drift apart silently.
  'home.why.eyebrow': 'Why Think Forward',
  'home.why.title': 'A regulated professional you can verify — not a sales pitch',
  'home.why.client.title': 'Client-Centered',
  'home.why.client.desc': 'A licensed consultant takes personal responsibility for your case — from the first call to final submission, in English or 中文. Your goals and timeline drive the strategy.',
  'home.why.transparent.title': 'Transparent Communication',
  'home.why.transparent.desc': 'Your fee is fixed in writing in the retainer agreement you sign — before any further payment, with no hidden fees. You always know the cost and the next step.',
  'home.why.rcic.title': 'RCIC Licensed Expertise',
  // The two register facts, under the same wording ceiling `home.trust.*` carries:
  // docs/research/register-facts-2026-07-29.md transcribes the licence start
  // (2021-11-03, Class L2, Active) and the four empty discipline tables. Widen
  // this and homeFace.test.tsx §k goes red against that file, exactly as (b) does.
  'home.why.rcic.desc': 'Licensed since 2021 · zero disciplinary record — verify anytime on the CICC Public Register.',
  'home.why.honest.title': 'Honest Screening',
  'home.why.honest.desc': 'If your case isn\'t ready, we\'ll say so before you spend a dollar — and tell you exactly what would make it ready. Whether an application succeeds is decided by IRCC alone, and no licensed consultant may claim otherwise.',

  // ── [T244] THE PATHWAY-FINDER PROMO — the ground truth's own third section ───
  //
  // This whole band was missing from the site AND from every task card until the
  // completeness audit started counting blocks instead of pages (T242 run 1 →
  // `docs/design/zip-parity-map.md` §5 新发现 ①). The homepage advertised the free
  // assessment in a hero button and then never said what the thing IS: three
  // steps, ten minutes, answers that stay in the reader's own browser. That is
  // the objection-answering copy, and it was sitting in the reference all along.
  //
  // Verbatim from `site/index.html` §PATHWAY FINDER PROMO (zh → `site/zh/index.html`),
  // `home.threeStep.note` included: that note is the hero's `btn-note` idiom in its
  // FULL form. The hero's shorter note (`pathwayFinder.badge`) is untouched — the
  // hero is outside this card's write-face — so the two read as a subset and its
  // superset, and only this one carries the browser-local clause.
  'home.threeStep.eyebrow': 'Free tool',
  'home.threeStep.title': 'Find your immigration pathway in three steps',
  'home.threeStep.lead': 'Answer questions about your goal, education, language and work experience — get the pathways that fit you, an indicative CRS estimate, and a clear next step.',
  'home.threeStep.step1.title': 'Answer questions',
  'home.threeStep.step1.desc': 'About your goal, education, language and work experience — ~10 minutes, in your browser.',
  'home.threeStep.step2.title': 'See your pathways',
  'home.threeStep.step2.desc': 'Express Entry, BC PNP Tech, study-to-PR, family sponsorship — with an indicative CRS estimate.',
  'home.threeStep.step3.title': 'Talk to an RCIC',
  'home.threeStep.step3.desc': 'Confirm your pathway with a licensed consultant before you spend money on applications.',
  'home.threeStep.cta': 'Start free assessment',
  'home.threeStep.note': 'Free · ~8–10 min · no sign-up · your answers stay in your browser',

  'home.consultant.title': 'Your Immigration Consultant',
  'home.consultant.name': 'Yansi He',
  'home.consultant.license': 'Founder & Principal Consultant, Licensed RCIC R708210',
  'home.consultant.location': 'Based in Greater Vancouver (Burnaby)',
  'home.consultant.intro': 'As the founder and principal consultant of Think Forward Immigration Consulting, Yansi He brings extensive experience in helping tech professionals and their families achieve their Canadian immigration goals.',
  // ── [T262] THE FOUR CHECK-LIST ROWS — frames only; the facts are READ ──────
  //
  // run2 §5b judged this block ⚠ on two counts: "v1 四条营销句" and "块内零外链".
  // Both are these four values. Two of them said nothing a stranger could check
  // ("Focused experience with tech-sector applicants" / "Committed to Honest,
  // Transparent & Culturally Sensitive Service"), and the block published no
  // register link at all — the one run1 credited it with is in the hero.
  //
  // ⛔ WHAT IS NOT IN THESE STRINGS, AND WHY. The two CICC register facts. They
  // live in `home.trust.licensedSince` / `home.trust.noDiscipline` (transcribed
  // in docs/research/register-facts-2026-07-29.md, frozen against it by T234 (b))
  // and `page.tsx` renders THOSE keys into rows 1 and 3, with the value here as
  // the frame around the fact:
  //   row 1 → "RCIC licensed since 2021 · regulated by CICC"
  //   row 3 → "Zero disciplinary record · verifiable on the CICC Public Register ↗"
  // A regulated claim about a MUTABLE register has to be retractable in one
  // edit; a second copy typed here would outlive that edit. `homeFace` §m fails
  // if either fact appears in this dictionary block, which is the paste-detector.
  //
  // Rows 2 and 4 carry no register fact and ARE the ground truth's own `<li>`
  // text nodes, verbatim (`site/index.html` §CONSULTANT) — §m asserts exact
  // membership, so a tidy-up here stops matching its source. Row 4's language
  // pair is also the trust strip's token (`home.credibility.bilingual`).
  'home.consultant.qual1': 'regulated by CICC',
  'home.consultant.qual2': 'Specialist in Tech & STEM immigration pathways',
  'ui.flipCard.flipBack': 'Flip back',
  // the register anchor's own label — the whole clause is the link, so a screen
  // reader's link list says what the link verifies rather than "CICC" alone
  'home.consultant.qual3': 'verifiable on the CICC Public Register',
  'home.consultant.qual4': 'Bilingual service: English · 中文',
  'home.consultant.readMore': 'Read Full Bio',
  'home.consultant.desc': 'As the founder and principal consultant of Think Forward Immigration Consulting, Yansi He brings extensive experience in helping tech professionals and their families achieve their Canadian immigration goals. Having successfully guided numerous employees from leading technology companies through complex immigration pathways, Yansi understands the unique challenges faced by skilled workers in today\'s competitive landscape. With a commitment to honest, transparent, and culturally sensitive service, Yansi established Think Forward to help more individuals and families navigate their Canadian immigration journey with confidence and clarity.',
  
  // [T262] The ground truth's heading (`site/index.html` §SERVICES) in place of
  // the v1 "Our Services" — run2 §5b's second index finding, and the only word
  // left in this section that was still ours rather than the reference's. It
  // names what the firm does instead of whose services they are, and it now
  // joins the T240 verbatim membership set in `homeFace` §i, which is where a
  // revert to the v1 heading turns red.
  'home.services.title': 'Immigration services',
  // [T240] THE SIX SERVICE CARDS — one per service page this site actually ships.
  //
  // The four keys that used to live here (`pr` / `work` / `family` / `lmia`) were
  // the Readdy-era CATEGORIES — "PR Pathways", "Work & Study Permits" — a
  // taxonomy that matched nothing else on the site: the nav, the footer and
  // /services all fan out to the same six program pages, and the homepage was
  // the one surface still sorting the firm's work into four buckets a reader
  // could not click through to. They are retired here, not renamed.
  //
  // ⚠ TRANSCRIPTION, NOT AUTHORSHIP. Every string below is a verbatim text node
  // of the ground truth, and `homeFace.test.tsx` §i re-reads those files on every
  // run, so a "tidy-up" here stops matching its source:
  //   en → docs/design/rebuild-v0b7764f/site/index.html §SERVICES ∪ site/services.html
  //   zh → docs/design/rebuild-v0b7764f/site/zh/index.html §服务
  //
  // WHY THE SET IS NOT THE EN INDEX'S SET VERBATIM. The ground truth disagrees
  // with itself on ONE card: `site/index.html` ships "LMIA & Employer Support"
  // linking to the services hub, while `site/zh/index.html` and `site/services.html`
  // both ship "C11 Entrepreneur Work Permit" in that slot, linking to a program
  // page. T240 asks for six cards 对应六专页 — corresponding to the six service
  // pages — and only the C11 set does that (`/services/lmia` does not exist here).
  // Two of the three ground-truth listings agree; the card's own words settle the
  // third. Recorded on T240 as a premise wobble, not resolved silently.
  'home.services.eyebrow': 'What we do',
  // `%LINK%` marks where the pricing-page anchor sits in the sentence. It is NOT
  // an i18next placeholder — `{{…}}` would make i18next warn and leave the braces
  // on the page. Keeping the whole sentence (punctuation and spacing included) as
  // ONE string per language is what stops either half drifting out of the ground
  // truth on its own; `home.services.leadLink` is the anchor's own text.
  // [T260 · B1-am2] the range clause leaves WITH the ranges (2026-07-31) —
  // the pointer must never outlive its target. Pre-B1 diverged form restored.
  // [T280 · S2] B1-am2 原句 → Q5 新裁 2026-08-02 owner 解钉改锚: the owner
  // EXPLICITLY unpinned the B1-am2 verbatim form (claims-audit §5-R, log 709 —
  // "改锚到合同") after it collided with the Q5 ruling (the "written fixed
  // quote" document does not exist; the retainer agreement does). The sentence
  // is now the T275 canonical pair in its home slot-compressed form, keeping
  // the %LINK% pointer to the pricing page. homeFace §i pins it byte-for-byte.
  'home.services.lead': 'Your full-service fee is fixed in writing in the retainer agreement you sign — before any further payment. See our %LINK%.',
  'home.services.leadLink': 'pricing page',
  'home.services.learnMore': 'Learn more',
  'home.services.expressEntry.title': 'Express Entry — STEM & CEC',
  'home.services.expressEntry.desc': 'Federal skilled immigration through category-based STEM draws and Canadian Experience Class. Indicative CRS estimate before you commit.',
  'home.services.bcPnpTech.title': 'BC PNP Tech',
  'home.services.bcPnpTech.desc': 'Provincial nomination for tech workers in BC — a permanent program with targeted weekly invitations for eligible occupations.',
  'home.services.spousal.title': 'Spousal & Family Sponsorship',
  'home.services.spousal.desc': 'Bring your spouse, partner or dependent children to Canada — inland and outland applications prepared line by line.',
  'home.services.pgwp.title': 'Study Permits & PGWP',
  'home.services.pgwp.desc': 'Study permits with PAL requirements, PGWP applications, and study-to-PR pathway planning from day one.',
  'home.services.c11.title': 'C11 Entrepreneur Work Permit',
  'home.services.c11.desc': 'LMIA-exempt work permits for entrepreneurs starting or buying a Canadian business — assessed on the benefit your business brings to Canada.',
  'home.services.superVisa.title': 'Parents & Grandparents Super Visa',
  'home.services.superVisa.desc': 'Long-term visitor status for parents and grandparents — income requirements and insurance handled correctly.',
  'home.services.view': 'View All Services',
  
  // ── [T244] THE CLOSING BAND — the ground truth's copy on T240's teal-50 form ──
  //
  // The form does not move (teal-50, hairline top and bottom, heading left,
  // actions right — T240 shipped it). Three things about the COPY do:
  //
  //   · the heading was "Ready to Start Your Journey?", which is what a template
  //     says. The ground truth's asks the reader's actual question and answers it
  //     with the free rung: "Not sure where to start? Take the free 10-minute
  //     assessment first."
  //   · `home.cta.desc` is RETIRED, not re-worded. The ground truth's band has no
  //     second line, and the one this site carried ("take the first step toward
  //     your Canadian dream") is the register the whole re-dress is leaving.
  //     t95's HOME_KEYS drops it in the same commit — a retired key that stays in
  //     the file is a revert waiting for a merge conflict (T240's precedent).
  //   · the band now carries BOTH rungs of the ladder in the ground truth's own
  //     order (I29): the free assessment is the ONE solid primary, and booking
  //     the paid consultation is the quieter secondary beside it. Before this, a
  //     reader who was not ready to pay had nothing to click in the closing band.
  //
  // ⛔ THE MONEY TOKEN — a recorded divergence, and the reason it is safe. The
  // ground truth says "Book a $99 consultation"; this says "CAD $99", which is the
  // form every other money string on this site publishes (`pricing.consultation.price`,
  // the booking flow's ten strings) and the form `priceConfig.CONSULTATION.display`
  // holds. A bare "$99" on a Canadian firm's site is the ambiguity the CAD prefix
  // exists to remove. `homeFace` §k asserts this string's money token IS
  // `CONSULTATION.display`, so the dictionary can never drift from the amount
  // Stripe actually charges — which is the property the verbatim form would lack.
  'home.cta.title': 'Not sure where to start? Take the free 10-minute assessment first.',
  'home.cta.assess': 'Start your free assessment',
  'home.cta.button': 'Book a CAD $99 consultation',
  
  // [T240] THE CLIENT-STORIES SECTION, IN ITS TWO STATES.
  //
  // `eyebrow` + `title` are the ground truth's own section head
  // (site/index.html §TESTIMONIALS PREVIEW), and they render ONLY when the
  // consent gate in `src/pages/testimonials/stories.ts` has something to show.
  // Today it has nothing — CV-34 records the written consent for every one of
  // those six quotes as still owed — so the section renders its stand-in state
  // instead, whose copy comes from `src/pages/testimonials/copy.ts` (the A2
  // three-layer architecture, already live on /testimonials). The heading is
  // gate-driven for the same reason the cards are: "Real cases, told with our
  // clients' written permission" over a section showing no client's words would
  // be a claim about material that is not on the page.
  //
  // `home.success.subtitle` is RETIRED: the ground truth's preview section has
  // no sub-heading, and the old string ("Real experiences from clients who
  // achieved their Canadian immigration goals…") is an outcome narrative about
  // the very stories CV-34 took down.
  'home.success.eyebrow': 'Client stories',
  'home.success.title': "Real cases, told with our clients' written permission",
  // The ground truth's own `.text-link-arrow` label. The zh side has no
  // /testimonials page in the reference build (its comment says the page is a
  // later batch), but this site ships /zh/testimonials — so the zh pair is a
  // translation of this string, not a transcription. Flagged as such on T240.
  'home.success.readMore': 'Read more client stories',

  // [T235] OUR COMMITMENTS — the ground truth's own heading and link label
  // (site/index.html §OUR COMMITMENTS; the zh pair is transcribed from
  // site/zh/index.html, not translated here). The commitments THEMSELVES are
  // NOT keys: they live in src/pages/pricing/priceConfig.ts as one array both
  // pages import, so the promise cannot say two different things in two places.
  'home.commitments.title': 'Our commitments — in writing, not in adjectives',
  'home.commitments.priceList': 'See the full price list',

  // About Page
  'about.hero.title': 'About Think Forward Immigration',
  'about.hero.subtitle': 'Honest, transparent, and client-centered Canadian immigration guidance',
  
  'about.consultant.title': 'Meet Your Consultant',
  'about.consultant.subtitle': 'Yansi He, RCIC R708210',
  'about.consultant.desc': 'Yansi He is the founder and principal consultant of Think Forward Immigration Consulting, bringing a wealth of experience as a Licensed Regulated Canadian Immigration Consultant (RCIC). With a proven track record of successfully assisting tech professionals and skilled workers from leading companies, Yansi has developed deep expertise in navigating complex immigration pathways including Express Entry, Provincial Nominee Programs, and LMIA-supported work permits. Her professional approach focuses on clarity, accuracy, and supportive client experience. Understanding firsthand the challenges faced by immigrants, Yansi founded Think Forward with a mission to provide personalized guidance tailored to each client\'s unique situation and goals, helping more people achieve their Canadian dreams with confidence.',
  
  'about.values.title': 'Our Core Values',
  'about.values.integrity.title': 'Integrity',
  'about.values.integrity.desc': 'We provide honest assessments and realistic expectations',
  'about.values.transparency.title': 'Transparency',
  'about.values.transparency.desc': 'Clear communication about processes, timelines, and costs',
  'about.values.cultural.title': 'Cultural Sensitivity',
  'about.values.cultural.desc': 'Respectful service for clients from all backgrounds',
  'about.values.innovation.title': 'Innovation',
  'about.values.innovation.desc': 'Strategic thinking and creative solutions for complex cases',
  'about.values.success.title': 'Client Success',
  'about.values.success.desc': 'Your success is our success',
  
  'about.approach.title': 'Our Approach',
  'about.approach.desc': 'We believe in clear explanations, respectful communication, and step-by-step guidance. Our goal is to help you understand every stage of the immigration process, empowering you to make informed decisions about your future in Canada. We take the time to listen to your story, assess your options, and develop a personalized strategy that aligns with your goals.',
  
  'about.success.title': 'Client Success Stories',
  'about.success.subtitle': 'Real experiences from clients who achieved their Canadian immigration goals with our guidance',
  'about.success.case1.quote': 'Efficient and professional service - helped me get my work permit in record time! The guidance was clear and the process was seamless.',
  'about.success.case1.name': 'Sarah Chen',
  'about.success.case1.type': 'Work Permit Client',
  'about.success.case2.quote': 'Yansi\'s strategy for my PR application was perfect. She understood my tech background and helped me maximize my CRS score. Now I\'m a permanent resident!',
  'about.success.case2.name': 'Raj Patel',
  'about.success.case2.type': 'Express Entry Client',
  'about.success.case3.quote': 'Outstanding support throughout the entire family sponsorship process. They made a complex process feel manageable and kept us informed every step of the way.',
  'about.success.case3.name': 'Maria Garcia',
  'about.success.case3.type': 'Family Sponsorship Client',
  'about.success.readMore': 'Read More Success Stories',
  
  // Contact Page
  'contact.hero.title': 'Contact Us',
  'contact.hero.subtitle': 'Get in touch to discuss your Canadian immigration journey',
  
  'contact.form.title': 'Send Us a Message',
  'contact.form.notice': 'For immediate answers to common questions, use our AI assistant in the bottom-right corner. For case assessments, please book a consultation.',
  'contact.form.inquiryType': 'Inquiry Type',
  'contact.form.selectInquiryType': 'Select inquiry type',
  'contact.form.inquiryTypes.consultation': 'Booking / consultation question',
  'contact.form.inquiryTypes.general': 'General question',
  'contact.form.inquiryTypes.employer': 'Employer / hiring (LMIA, GTS)',
  'contact.form.inquiryTypes.support': 'Existing client support',
  'contact.form.inquiryTypes.partnership': 'Partnership / media / other',
  'contact.form.name': 'Your Name',
  'contact.form.email': 'Your Email',
  'contact.form.briefSummary': 'Brief Summary (max 200 characters)',
  'contact.form.noSensitiveInfo': 'Please do not include sensitive personal information here.',
  'contact.form.minCharHint': '💡 Please provide at least 50 characters of genuine consultation details, such as: your background, immigration goals, specific questions, etc.',
  'contact.form.message': 'Your Message',
  'contact.form.messagePlaceholder': 'Please briefly describe your inquiry...',
  'contact.form.disclaimer': 'I understand that Think Forward Immigration does not provide specific legal advice via email. This form is for administrative or consultation booking purposes only.',
  'contact.form.disclaimerIAgree': 'I have read and agree to the',
  'contact.form.termsOfService': 'Terms of Service',
  'contact.form.and': 'and',
  'contact.form.privacyPolicy': 'Privacy Policy',
  'contact.form.noRelationship': ', and I understand that submitting this form does not create a consultant-client relationship.',
  'contact.form.submit': 'Send Message',
  'contact.form.success': 'Thank you! Your message has been sent successfully.',
  'contact.form.error': 'Sorry, there was an error sending your message. Please try again.',
  'contact.form.errorMissingFields': 'Please fill in all required fields.',
  'contact.form.errorMessageTooShort': 'Please provide a bit more detail (at least 50 characters) — e.g. your background, immigration goal, and specific question — so we can help you properly.',
  'contact.form.errorCheckboxRequired': 'Please check the box to agree to the Terms of Service and Privacy Policy before submitting.',
  'contact.form.errorSpam': 'Your message appears too brief or generic. Please provide more specific details about your immigration inquiry (at least 50 characters with meaningful content).',
  'contact.form.charCount': 'characters remaining',
  'contact.form.charNeeded': '⚠️ Need at least {count} more characters',
  'contact.form.charSufficient': '✓ Length requirement met',
  'contact.form.errorInvalidEmail': 'Please enter a valid email address.',
  'contact.form.errorInvalidData': 'Please check your input and try again.',
  'contact.form.errorTooManyRequests': 'Too many requests. Please wait a moment before trying again.',
  'contact.form.errorServerError': 'Server error. Please try again later.',
  'contact.form.errorNetwork': 'Network error. Please check your internet connection and try again.',
  'contact.form.errorGeneric': 'An error occurred',
  
  'contact.info.title': 'Contact Information',
  'contact.info.consultant': 'Principal Consultant, RCIC R708210',
  'contact.info.email': 'Email',
  'contact.info.phone': 'Phone',
  'contact.info.location': 'Location',
  'contact.info.vancouver': 'Greater Vancouver (Burnaby), BC',
  
  'contact.skipWait.title': 'Skip the Wait',
  'contact.skipWait.desc': 'Ready to start immediately? Book a direct session with our RCIC.',
  'contact.skipWait.button': 'Book Consultation',
  
  'contact.cta.title': 'Ready to Begin?',
  'contact.cta.desc': 'Book a consultation to discuss your immigration goals and explore your pathway to Canada.',
  'contact.cta.button': 'Book a Consultation',
  
  // Footer
  'footer.company': 'Think Forward Immigration Consulting',
  'footer.license': 'RCIC License: R708210',
  'footer.description': 'Based in Greater Vancouver (Burnaby), providing strategic immigration solutions for tech professionals and families worldwide.',
  // [T235] The footer's first column is now the ground truth's "Our Services"
  // (one link per service page). Only the HEADING is a key — the six labels are
  // read from src/data/programsData.json so they cannot drift from the pages
  // they open.
  'footer.services.title': 'Our Services',
  'footer.company.title': 'Company',
  'footer.contact.title': 'Contact',
  'footer.contact.address': 'Greater Vancouver (Burnaby), BC',
  'footer.contact.rcic': 'Regulated Canadian Immigration Consultant (RCIC)',
  'footer.quickLinks': 'Quick Links',
  'footer.social': 'Follow Us',
  'footer.copyright': '© {{year}} Think Forward Immigration Consulting. All rights reserved.',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.powered': 'Website Builder',
  
  // Pricing Page
  pricing: {
    hero: {
      title: 'Service Fees & Pricing',
      subtitle: 'We believe in transparent and fair pricing. Every case is unique, and we provide personalized quotes based on your specific situation.',
    },
    consultation: {
      title: 'Initial Consultation Package',
      subtitle: 'One-on-One with Professional RCIC',
      price: 'CAD $99',
      currency: 'Canadian Dollars',
      included: 'Package Includes',
      feature1: {
        title: '45-Minute Video Call with RCIC',
        desc: 'In-depth discussion with licensed immigration consultant',
      },
      feature2: {
        title: 'Eligibility Assessment',
        desc: 'Comprehensive evaluation of your immigration eligibility',
      },
      feature3: {
        title: 'Strategic Roadmap',
        desc: 'Customized immigration strategy and timeline planning',
      },
      feature4: {
        title: 'Fee Fully Deductible',
        desc: 'Upon retaining for full service, consultation fee is fully credited',
      },
      bonus: {
        title: 'Special Offer:',
        desc: 'If you decide to retain our full immigration services, this consultation fee will be fully deducted from your service fees.',
      },
      cta: 'Book Consultation',
    },
    process: {
      title: 'Our Service Process',
      desc: 'Transparent, professional, and efficient three-step process to ensure your immigration journey is clear and well-defined',
      step1: {
        title: '1. Paid Consultation',
        desc: 'We analyze your specific situation, assess eligibility, and develop initial strategy',
      },
      step2: {
        title: '2. Strategy & Quote',
        desc: 'We provide a tailored immigration plan and transparent fee breakdown',
      },
      step3: {
        title: '3. Retainer Agreement',
        // [T280 · Q9] "immediately start your application process" → the
        // visible action (owner, claims-audit §5-R Q9, 2026-08-02): case
        // creation is a manual step by the consultant, so "immediately" had no
        // machine meaning — the invite email (`invite-send`) is the action a
        // client actually sees, and that is what the sentence now promises.
        desc: 'Once the agreement is signed, we open your file and send your secure intake link.',
      },
    },
    philosophy: {
      title: 'Our Pricing Philosophy',
      desc1: 'As licensed immigration consultants, we understand that every client\'s situation is unique. The complexity of cases, time required, and resources needed vary significantly from person to person.',
      desc2: 'Therefore, we don\'t offer one-size-fits-all pricing. Instead, we provide customized quotes after thoroughly understanding your circumstances. This ensures you only pay for the services you truly need and receive maximum value.',
    },
    whyQuote: {
      title: 'Why Personalized Quotes?',
      desc: 'Every immigration case has its unique characteristics. We believe transparent and fair pricing should be based on your actual needs',
      reason1: {
        title: 'Varying Complexity',
        desc: 'Some cases may require additional document preparation, translation, or legal analysis, all of which affect service costs',
      },
      reason2: {
        title: 'Fair Pricing Principle',
        desc: 'Simple cases shouldn\'t pay for complex case fees. We ensure every client receives fair pricing',
      },
      reason3: {
        title: 'Transparent Communication',
        desc: 'During consultation, we\'ll explain fee structures in detail so you understand the value of each service',
      },
    },
    howToGet: {
      title: 'How to Get Your Personalized Quote',
      subtitle: 'Three simple steps - let our AI assistant help you book a consultation',
      step1: {
        title: 'Browse Our Services',
        desc: 'Visit our "Services" page to explore the various immigration services we offer and find the program that best suits your needs',
        link: 'View All Services',
      },
      step2: {
        title: 'Chat with AI Assistant',
        desc: 'Click the AI assistant button at the bottom right of the page. Our AI assistant will communicate with you in your language (Chinese/English)',
        note: 'Supports Chinese and English',
      },
      step3: {
        title: 'Book a Consultation',
        desc: 'The AI assistant will help you schedule a consultation with our licensed immigration consultant to discuss your specific situation and receive a personalized quote',
        note: 'CAD $99 Consultation',
      },
      cta: 'Chat with AI Assistant Now',
      note: 'Or visit our Services page directly to choose the service you\'re interested in',
    },
    services: {
      title: 'Service Categories',
      subtitle: 'We offer comprehensive Canadian immigration services. Click to learn more and get a quote',
    },
    pr: {
      title: 'Permanent Residency Programs',
      express: {
        title: 'Express Entry (FSW/CEC/FST)',
      },
      pnp: {
        title: 'Provincial Nominee Programs',
      },
      family: {
        title: 'Family Sponsorship',
      },
      economic: {
        title: 'Economic Immigration Programs',
      },
    },
    work: {
      title: 'Work Permits',
      lmia: {
        title: 'LMIA-Supported Work Permits',
      },
      permit: {
        title: 'Employer-Specific Work Permits',
      },
      openPermit: 'Open Work Permits',
      extension: 'Work Permit Extensions',
    },
    study: {
      title: 'Study Permits',
      permit: {
        title: 'Study Permit Applications',
      },
      pgwp: {
        title: 'Post-Graduation Work Permit (PGWP)',
      },
      extension: 'Study Permit Extensions',
      coop: 'Co-op Work Permits',
    },
    other: {
      title: 'Other Services',
      consultation: {
        title: 'Immigration Assessment Consultation',
      },
      review: {
        title: 'Case Review & Advice',
      },
      visitor: 'Visitor Visa Extensions',
      citizenship: 'Citizenship Applications',
    },
    getQuote: 'Get a Quote',
    included: {
      title: 'What\'s Included in Our Services',
      item1: {
        title: 'Complete Case Preparation',
        desc: 'From initial assessment to final submission, we handle all necessary documents and forms',
      },
      item2: {
        title: 'Full Professional Support',
        desc: 'Throughout the application process, you\'ll receive continuous guidance from licensed consultants',
      },
      item3: {
        title: 'Quality Assurance',
        desc: 'We prepare every application document to the highest standard to maximize the strength of your application',
      },
    },
    howItWorks: {
      title: 'How to Get Your Quote',
      step1: {
        title: 'Contact Us',
        desc: 'Reach out via phone, email, or online form to briefly describe your immigration needs',
      },
      step2: {
        title: 'Initial Consultation',
        desc: 'We will start with a consultation to understand your background, goals, and specific situation',
      },
      step3: {
        title: 'Receive Personalized Quote',
        desc: 'Based on your case assessment, we\'ll provide detailed service scope and fee breakdown',
      },
      step4: {
        title: 'Begin Partnership',
        desc: 'Once you accept the quote, we\'ll immediately start working toward your immigration dreams',
      },
    },
    notes: {
      title: 'Important Notes',
      note1: 'All fees are for professional services only and do not include government application fees, translation costs, notarization fees, or other third-party expenses.',
      // [T275 · Q10] `note2` ("flexible payment plans…") RETIRED from both
      // dictionaries — owner ruling §5-R Q10: the practice does not offer
      // instalment plans, and the repo holds zero instalment machinery. The
      // /pricing notes list re-flows to the two notes that are true.
      note3: 'The initial consultation is a paid CAD $99 session. We believe you should fully understand all options before making a decision.',
    },
    cta: {
      // T246 lands T245's parked ⑥: title + button are the ground truth's CTA
      // band verbatim (site/pricing.html §CTA BAND). The zip band has no desc;
      // ours states the two facts that are true before any payment — the v1
      // sentence's plural "consultants" was simply false (one consultant).
      // [T280 · S4] the desc's second fact re-anchors to the retainer
      // agreement (owner, claims-audit §5-R Q5 family — the "written, fixed
      // quote" document does not exist; the contract does): T275 canonical
      // pair, slot-compressed, both invariants kept (carrier = the retainer
      // agreement you sign; timing = before any further payment).
      title: 'Start with a $99 consultation — credited when you retain us.',
      desc: 'A 45-minute video call with the licensed consultant — and your full-service fee fixed in writing in the retainer agreement you sign, before any further payment.',
      button: 'Book a $99 consultation',
    },
  },

  // Testimonials Page
  testimonials: {
    hero: {
      title: 'Client Success Stories',
      subtitle: 'Real experiences from clients who achieved their Canadian immigration goals with our expert guidance and strategic approach.'
    },
    filter: {
      all: 'All Cases',
      pr: 'Permanent Residency',
      work: 'Work Permits',
      family: 'Family Sponsorship',
      business: 'Business Immigration',
      citizenship: 'Citizenship'
    },
    cases: {
      case1: {
        role: 'Software Engineer',
        service: 'BC PNP Tech',
        story: 'Honestly, I was panicking because my work permit was expiring and my CRS score wasn\'t high enough. I was running out of time and options. Yansi was a lifesaver! She stepped in, negotiated with my HR for the PNP support, and got everything submitted so fast. Her strategic approach and quick action made all the difference. Getting that nomination email in just 3 weeks felt like a miracle. So relieved! Now working in Vancouver tech industry with PR approved.'
      },
      case2: {
        role: 'Marketing Manager',
        service: 'Spousal Sponsorship',
        story: 'After getting refused once before, my wife and I were devastated and terrified to try again. We didn\'t know what went wrong or how to fix it. Yansi completely rebuilt our confidence. The Letter of Explanation she wrote was incredible—it addressed every single doubt the officer had. She knew exactly what to say. Today my wife landed in Vancouver, and I can\'t thank Think Forward enough for bringing our family together. Best decision we ever made.'
      },
      case3: {
        role: 'Graphic Designer',
        service: 'Study to PR (CEC)',
        story: 'I almost missed my chance for PR! I didn\'t realize my PGWP was expiring so soon. I was about to lose my status in Canada. Yansi identified a critical window to apply for Canadian Experience Class (CEC) just in time. Her strategic timing and meticulous application preparation saved everything. I don\'t even want to think about what would have happened without her sharp eyes. Best investment ever. Now running my own design studio in Toronto.'
      },
      case4: {
        role: 'Retired Parents',
        service: 'Parents Super Visa',
        story: 'We wanted to spend more time with our grandchildren in Vancouver, but regular visitor visas were too restrictive with only 6-month stays. Yansi\'s team prepared all the Super Visa documents for us, including medical exams and insurance arrangements. Everything was handled professionally and carefully. We received our 10-year Super Visa on the first try! Now we can visit our family for up to 5 years at a time and truly be part of our grandchildren\'s lives. We are so grateful!'
      },
      case5: {
        role: 'Civil Engineer',
        service: 'LMIA & Work Permit',
        story: 'My employer was willing to sponsor me but had no clue how to do an LMIA. They were about to give up because the process seemed too complicated. Yansi stepped in and guided their HR through every confusing step. She made it easy for them and handled all the complex compliance requirements professionally. I finally got my work permit approved thanks to her persistence. Positive LMIA secured. Now on pathway to permanent residence through Canadian work experience.'
      },
      case6: {
        role: 'Entrepreneur',
        service: 'Business Immigration',
        story: 'Moving my business to Canada seemed like a massive headache. I didn\'t know where to start with the immigration requirements for entrepreneurs. Yansi mapped out the whole C11 process for me and made it look easy. Her business background really helped in drafting the business plan and strategy. Now my company is up and running in Vancouver! Business successfully established. Work Permit granted. Now employing Canadian workers.'
      },
      case7: {
        role: 'Long-term Resident',
        service: 'Citizenship Application',
        story: 'My travel history was a mess, and I was so scared I\'d miscalculated my physical presence days and get rejected. I had so many international trips. Yansi and her team went through my passport stamps one by one to ensure 100% accuracy. She cross-referenced everything meticulously—flight records, employment records, everything. Holding that Canadian passport finally feels amazing. Canadian Citizenship granted smoothly without any issues. Proud Canadian citizen after 6 years in Canada.'
      },
      case8: {
        role: 'French Speaker',
        service: 'Francophone Mobility',
        story: 'I had no idea my French skills could get me a work permit without an LMIA! I was preparing for a lengthy LMIA application unnecessarily. Yansi told me about this Francophone program and got my application ready in no time. It was the easiest process ever. Merci beaucoup, Yansi! Work Permit approved in record time without LMIA. Now working in Montreal and contributing to Canada\'s Francophone community.'
      }
    },
    cta: {
      title: 'Start with an honest assessment',
      desc: 'Book a paid consultation with a licensed RCIC: we review your situation, explain which pathways apply to it, and tell you plainly what we can and cannot do.',
      button: 'Book Consultation'
    }
  },

  // FAQ Page
  faq: {
    hero: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to common questions about Canadian immigration and our services. Can\'t find what you\'re looking for? Contact us directly.'
    },
    sidebar: {
      title: 'Categories'
    },
    label: {
      source: 'Official source',
      asOf: 'Information current as of June 2026 — rules change frequently; always confirm on the official IRCC website (canada.ca).'
    },
    categories: {
      general: 'General Questions',
      pr: 'Permanent Residency',
      work: 'Work Permits',
      study: 'Study Permits',
      family: 'Family Sponsorship',
      process: 'Process & Timeline',
      trust: 'Trust & Working With Us',
      gettingStarted: 'Getting Started'
    },
    contact: {
      title: 'Still Have Questions?',
      desc: 'Book a consultation with our RCIC-licensed consultant for personalized answers to your immigration questions.',
      button: 'Book Consultation'
    }
  },

  footer: {
    mission: 'Your trusted partner in navigating Canadian immigration. We provide expert guidance and personalized solutions to help you achieve your Canadian dream.',
    company: {
      title: 'Company'
    },
    licensing: {
      title: 'Regulated & Contact',
      authorized: 'Yansi He is licensed and regulated by the College of Immigration and Citizenship Consultants (CICC), Licence R708210.',
      license: 'RCIC License:'
    },
    contact: {
      address: 'Greater Vancouver (Burnaby), BC'
    },
    copyright: '© {{year}} Think Forward Immigration Consulting. All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    powered: 'Website Builder'
  },

  // Terms of Service Modal
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'Last Updated: January 2025',
    close: 'Close',
    intro: {
      title: 'Introduction',
      content: 'Welcome to Think Forward Immigration Consulting. By accessing or using our website (thinkforwardic.com), you agree to be bound by these Terms of Service. Please read them carefully before using our services or website.'
    },
    disclaimer: {
      title: 'Important Legal Disclaimer',
      content1: 'The information provided on this website is for general informational purposes only and does NOT constitute legal advice, immigration consultation, or professional services.',
      content2: 'Immigration laws, regulations, processing times, and policies change frequently. While we strive to keep information current and accurate, we make no representations or warranties regarding the completeness, accuracy, reliability, or suitability of any information on this website.',
      content3: 'You should not rely on any information on this website as a substitute for professional immigration consultation. For specific advice regarding your immigration matter, please book a paid consultation with our licensed RCIC.'
    },
    relationship: {
      title: 'No Consultant-Client Relationship',
      content1: 'Merely browsing this website, submitting a contact form, or sending an email does NOT create a consultant-client relationship between you and Think Forward Immigration Consulting or Yansi He, RCIC.',
      content2: 'A formal consultant-client relationship is established only when: (1) we have conducted an initial consultation, (2) we have agreed in writing to represent you, and (3) you have signed a retainer agreement and paid the required fees.'
    },
    accuracy: {
      title: 'Accuracy of Information',
      content1: 'We make every effort to ensure that the information on this website is accurate and up-to-date. However, Canadian immigration laws, regulations, processing times, and policies are subject to frequent changes by Immigration, Refugees and Citizenship Canada (IRCC) and other government authorities.',
      content2: 'We do not guarantee that all information on this website reflects the most current legal developments. Always verify critical information with official government sources or through a paid consultation with our RCIC.'
    },
    copyright: {
      title: 'Intellectual Property and Copyright',
      content1: 'All content on this website, including but not limited to text, graphics, logos, images, videos, and software, is the property of Think Forward Immigration Consulting and is protected by Canadian and international copyright laws.',
      content2: 'You may not reproduce, distribute, modify, transmit, or use any content from this website for commercial purposes without our prior written consent. Personal, non-commercial use is permitted with proper attribution.'
    },
    thirdParty: {
      title: 'Third-Party Links',
      content: 'This website may contain links to third-party websites (including government websites, educational institutions, and other resources). We do not control or endorse these external sites and are not not responsible for their content, privacy policies, or practices. Accessing third-party links is at your own risk.'
    },
    liability: {
      title: 'Limitation of Liability',
      content: 'To the fullest extent permitted by law, Think Forward Immigration Consulting, Yansi He, and our affiliates shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of this website or reliance on any information provided herein. This includes, but is not limited to, damages for lost profits, data loss, or immigration application refusals.'
    },
    privacy: {
      title: 'Privacy and Data Protection',
      content: 'Your use of this website is also governed by our Privacy Policy. We are committed to protecting your personal information in accordance with Canadian privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA). Please review our Privacy Policy to understand how we collect, use, and protect your data.'
    },
    law: {
      title: 'Governing Law and Jurisdiction',
      content: 'These Terms of Service are governed by and construed in accordance with the laws of the Province of British Columbia and the federal laws of Canada applicable therein. Any disputes arising from these terms or your use of this website shall be subject to the exclusive jurisdiction of the courts of British Columbia.'
    },
    changes: {
      title: 'Changes to These Terms',
      content: 'We reserve the right to modify these Terms of Service at any time without prior notice. Changes will be effective immediately upon posting to this website. Your continued use of the website after any changes constitutes your acceptance of the revised terms. We encourage you to review these terms periodically.'
    },
    contact: {
      title: 'Contact Information',
      content: 'If you have any questions about these Terms of Service, please contact us:'
    }
  },

  // Privacy Policy Modal
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated: January 2025',
    close: 'Close',
    intro: {
      title: 'Introduction',
      content: 'Think Forward Immigration Consulting ("we," "our," or "us") is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. As a Regulated Canadian Immigration Consultant (RCIC) firm, we adhere to strict confidentiality and privacy standards mandated by the College of Immigration and Citizenship Consultants (CICC) and Canadian privacy laws.'
    },
    cicc: {
      title: 'CICC Confidentiality Standards',
      content1: 'As licensed members of the College of Immigration and Citizenship Consultants (CICC), we are bound by the CICC Code of Professional Conduct, which requires us to maintain the strictest confidentiality of all client information.',
      content2: 'We treat all client communications, documents, and case details as strictly confidential and privileged information. This confidentiality obligation continues even after our professional relationship ends.',
      content3: 'We will never share your personal information with third parties without your explicit consent, except as required by law or professional regulations.'
    },
    collection: {
      title: 'Information We Collect',
      intro: 'We collect information solely for the purpose of providing professional immigration consulting services. The types of information we may collect include:',
      item1: {
        title: 'Personal Identification Information',
        desc: 'Name, date of birth, passport details, contact information (email, phone, address), and other identity documents required for immigration applications.'
      },
      item2: {
        title: 'Immigration Case Details',
        desc: 'Educational credentials, work history, language test results, family information, travel history, and other details relevant to your immigration assessment and application.'
      },
      item3: {
        title: 'Financial Information',
        desc: 'Payment information for our services, proof of funds documentation, and other financial records required for immigration purposes.'
      },
      item4: {
        title: 'Website Usage Data',
        desc: 'When you visit our website, we may automatically collect certain technical information such as IP address, browser type, pages visited, and time spent on pages through cookies and similar technologies.'
      }
    },
    usage: {
      title: 'How We Use Your Information',
      intro: 'We use your personal information exclusively for legitimate immigration consulting purposes:',
      item1: 'Assessing your eligibility for Canadian immigration programs',
      item2: 'Preparing and submitting immigration applications on your behalf',
      item3: 'Communicating with you about your case and providing updates',
      item4: 'Corresponding with Immigration, Refugees and Citizenship Canada (IRCC) and other government authorities',
      item5: 'Maintaining accurate records as required by CICC regulations and Canadian law'
    },
    sharing: {
      title: 'Information Sharing and Disclosure',
      content1: 'We do NOT sell, trade, rent, or share your personal information with third parties for marketing purposes. Your information is treated with the highest level of confidentiality.',
      content2: 'We may only disclose your information in the following limited circumstances:',
      exception1: 'To government authorities (IRCC, CBSA, provincial immigration offices) as necessary for processing your immigration applications',
      exception2: 'To authorized third-party service providers (translators, credential evaluators, medical examiners) only when necessary for your application and only with your consent',
      exception3: 'When required by law, court order, or legal process, or to protect our legal rights'
    },
    security: {
      title: 'Data Security',
      content1: 'We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. Our security practices include:',
      measure1: 'Encrypted data transmission (SSL/TLS) for all online communications',
      measure2: 'Secure, password-protected file storage systems with restricted access',
      measure3: 'Regular security audits and updates to our systems',
      measure4: 'Staff training on confidentiality obligations and data protection',
      content2: 'While we take every reasonable precaution to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to using best practices to safeguard your data.'
    },
    retention: {
      title: 'Data Retention',
      content1: 'We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected and to comply with legal and regulatory requirements.',
      content2: 'Under CICC regulations, we are required to maintain client files for a minimum of 6 years after the conclusion of our services. After this retention period, we securely destroy or anonymize your personal information.'
    },
    rights: {
      title: 'Your Privacy Rights',
      intro: 'You have the following rights regarding your personal information:',
      right1: 'Access: You may request access to the personal information we hold about you',
      right2: 'Correction: You may request correction of any inaccurate or incomplete information',
      right3: 'Withdrawal of Consent: You may withdraw consent for certain uses of your information (subject to legal and contractual obligations)',
      right4: 'Complaint: You may file a complaint with the Privacy Commissioner of Canada if you believe your privacy rights have been violated'
    },
    cookies: {
      title: 'Cookies and Tracking Technologies',
      content1: 'Our website uses cookies and similar technologies to improve user experience, analyze website traffic, and understand visitor behavior. Cookies are small text files stored on your device.',
      content2: 'You can control cookie settings through your browser preferences. However, disabling cookies may affect your ability to use certain features of our website.'
    },
    changes: {
      title: 'Changes to This Privacy Policy',
      content: 'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will post the updated policy on our website with a revised "Last Updated" date. We encourage you to review this policy periodically.'
    },
    contact: {
      title: 'Contact Us for Privacy Inquiries',
      content: 'If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal information, please contact us:'
    }
  },

  bookConsultation: {
    header: 'Book Your Immigration Strategy Session',
    subheader: 'Secure a 1-on-1 consultation with a Regulated Canadian Immigration Consultant (RCIC). Receive a personalized legal assessment and roadmap.',
    step1: {
      label: 'Review',
    },
    step2: {
      label: 'Select Time',
    },
    step3: {
      label: 'Payment',
    },
    processNote: {
      title: 'How It Works',
      desc: 'Complete your booking in three simple steps:',
      step1: 'Review the consultation package and agree to the terms.',
      step2: 'Pick a date and time that works for you. All times shown in your local timezone.',
      step3: 'Secure your spot with a CAD $99 payment. You will receive a receipt.',
    },
    consent: {
      title: 'Required Consent',
      termsPrefix: 'I have read and agree to the',
      termsLink: 'Terms of Service',
      termsSuffix: '',
      privacyPrefix: 'I have read and agree to the',
      privacyLink: 'Privacy Policy',
      privacySuffix: '',
      agreeAll: 'I agree to both the Terms of Service and Privacy Policy.',
      required: 'Please agree to the required terms above to continue.',
    },
    summary: {
      title: 'Your Appointment',
      date: 'Date',
      time: 'Time',
      name: 'Name',
      email: 'Email',
      fee: 'Consultation Fee',
      pacificTime: 'Pacific Time',
      yourLocalTime: 'your local time',
      consultant: 'Consultant',
    },
    heroAlt: 'Immigration consultation',
    timePicker: {
      // [T288] The hours are INTERPOLATED from the booking window (src/lib/slots.ts
      // → TimeSlotPicker), not typed: the 2026-09-15 ruling moved them once already
      // and a consultant can now move them again from /admin. "Pacific time" is the
      // owner's 2026-09-15 amendment — consultations are always Pacific.
      hint: 'Select a date and time for your 45-minute online consultation. Consultations run Mon-Fri, {{start}} - {{end}} Pacific time, and bookings open at least 48 hours in advance.',
      localTimeNote: 'Your local time is shown below each time slot.',
      availableFor: 'Available times for {{date}}',
      selectDate: 'Select a date',
      today: '(Today)',
      consultantTimeParen: '(Times shown in the consultant\'s timezone: {{tz}})',
      consultant: 'Consultant:',
      loading: 'Loading availability...',
      noSlots: 'No available slots for this date',
      tryAnother: 'Try another date or check back later',
      morning: 'Morning',
      afternoon: 'Afternoon',
      selectedTime: 'Selected Time',
      consultantTime: 'consultant\'s time ({{tz}})',
      yourLocalTime: 'Your local time:',
      yourName: 'Your Name',
      namePlaceholder: 'Enter your full name',
      continuePayment: 'Continue to Payment',
    },
    back: 'Back',
    payment: {
      title: 'Confirm Your Appointment',
      desc: 'A CAD $99 payment is required to secure your consultation slot. This fee is fully deductible if you retain our services for full immigration representation.',
      // [T275 · Q3] `receiptTitle` / `receiptDesc` RETIRED from both dictionaries
      // (owner ruling, claims-audit §5-R Q3): the sentence promised an "official
      // receipt … for IRCC submission" — a Stripe card receipt carries no
      // practice identity and does not serve that purpose, and whether even the
      // Stripe email is on is the owner's open check. T268 orphan discipline:
      // the render block went with the keys (book-consultation step 3).
      termsPrefix: 'I understand that the CAD $99 is a non-refundable consultation fee for a one-time Initial Assessment session. I agree to the',
      termsLink: 'Terms of Service',
      termsSuffix: '',
      privacyPrefix: 'I have read and agree to the',
      privacyLink: 'Privacy Policy',
      privacySuffix: 'and consent to the collection and use of my information for this consultation.',
      cta: 'Pay CAD $99 & Confirm Booking',
      processing: 'Processing...',
      checkboxRequired: 'Please agree to the payment terms and privacy policy to proceed.',
      secureNote: 'Payment processed securely by Stripe. We do not store your card details.',
      noTime: 'No appointment time found. Please go back to Step 2 and select a time slot before proceeding with payment.',
      emailLabel: 'Email address',
      emailNeeded: 'Email address is needed for payment receipt',
      emailPlaceholder: 'Enter your email address',
      emailWhy: 'Stripe requires an email to send your payment receipt and booking confirmation.',
    },
    cta: {
      step1: 'Select a Time Slot',
    },
    seo: {
      title: 'Book Consultation | CAD $99 Initial Assessment | Think Forward Immigration',
      description: 'Book a 45-minute one-on-one consultation with Licensed RCIC Yansi He. CAD $99 initial assessment includes eligibility evaluation and strategic roadmap.',
      keywords: 'book immigration consultation, RCIC consultation, Canadian immigration assessment, CAD $99 consultation, Vancouver immigration consultant booking',
    },
    disclaimer: {
      text: 'By scheduling, you agree to our',
      terms: 'Terms of Service',
      and: 'and',
      privacy: 'Privacy Policy',
    },
  },

  bookingSuccess: {
    title: 'Booking Confirmed!',
    subtitle: 'Thank you for your payment. Your consultation has been secured.',
    generating: 'Generating your meeting link...',
    generatingSlow: "Meeting link generation is taking longer than expected. Don't worry — you'll receive the link by email shortly.",
    meeting: {
      title: 'Your Video Meeting Room',
      desc: 'A private video meeting room has been created for your consultation. Save this link — you will need it to join at your scheduled time.',
      roomLabel: 'Meeting Link',
      joinNow: 'Open Meeting Room',
      noDownload: 'No downloads or account required. Works directly in your browser. Click the link at your appointment time to join.',
    },
    emailTitle: 'What happens next',
    emailDesc: 'You will receive three emails shortly:',
    emailItem1: 'A payment receipt from Stripe with transaction details.',
    emailItem2: 'An email confirmation from us with your appointment details and instructions.',
    emailItem3: 'Preparation guidelines to help you get ready for the consultation.',
    nextSteps: {
      title: 'What to Expect',
      step1: {
        title: 'Review Your Documents',
        desc: 'Gather your passport, resume, and any immigration-related documents for the consultant to review.',
      },
      step2: {
        title: 'Join the Video Call',
        desc: 'At your scheduled time, click the meeting link above to join the video consultation. No downloads needed.',
      },
      step3: {
        title: 'Receive Your Roadmap',
        desc: 'After the session, you will receive a written strategic roadmap with next steps.',
      },
    },
    cta: {
      services: 'Explore Our Services',
      home: 'Back to Home',
    },
    seo: {
      title: 'Booking Confirmed | Think Forward Immigration',
      description: 'Your consultation booking and payment have been confirmed.',
      keywords: 'booking confirmed, immigration consultation confirmed',
    },
  },

  about: {
    // [T216] `altCiccMember` retired with the RCIC designation-mark image it
    // labelled (CV-33). Its place is held by the same claim in text: the licence
    // number plus the register that can verify it (methodology verify-pill precedent).
    verify: {
      licence: 'RCIC R708210',
      register: 'Verify on the CICC public register',
      // [T243] The ground truth's "Verify before you hire" block
      // (site/about.html §VERIFY). Procedure only — it states no rule value the
      // register itself does not show, and its footnote's canada.ca link is the
      // one this repo already publishes in guidesData.json (never a new URL).
      // First person singular in the zip becomes the firm's voice: the page is
      // published by the firm, and a reader verifies a LICENCE, not a person.
      steps: {
        eyebrow: 'Verify before you hire',
        title: 'Don’t take our word for it — verify',
        lead: 'Anyone can print “licensed consultant” on a website; only the CICC public register tells you what is actually on file. Checking takes under a minute — before we talk, before you pay, before you share a single document.',
        s1: {
          title: 'Open the CICC public register',
          body: 'It is published by the regulator, not by us.',
        },
        s2: {
          title: 'Search the licence number or the name',
          body: 'Licence number R708210, or the name Yansi He.',
        },
        s3: {
          title: 'Read the status yourself',
          body: 'The profile shows the licence class, the start date, the status, and any disciplinary history there is to show.',
        },
        footnote:
          'If a representative hesitates when you ask for their licence number, that is your answer. For a fee, only a CICC-licensed consultant, a Canadian law-society lawyer or a Québec notary may advise or represent you — check anyone on canada.ca:',
        footnoteLink: 'Use an immigration representative — canada.ca',
      },
    },
    altConsultation: 'Professional consultation',
    altOffice: 'Think Forward Immigration Consulting office',
    // ── [T243] THE REGISTER FACTS ────────────────────────────────────────────
    // source_url: https://register.college-ic.ca/Public-Register-EN/Licensee/Profile.aspx?ID=15486
    // verified_at: 2026-07-29 — transcription: docs/research/register-facts-2026-07-29.md
    // Every value below is copied from that capture, NOT paraphrased: Licence
    // `Class L2 - RCIC`, Start `2021-11-03`, Status `Active`, and all four
    // discipline-family tables reading `There are no records`. The research
    // file's wording ceiling is obeyed to the letter — the licence claim carries
    // the DAY (so it is the register's fact, not a rounded one), and the
    // discipline claim is stated as what the register contained on a date, with
    // the date on the page and the deep link beside it, so a reader can refute
    // it in one click. If the register ever shows a record or a non-Active
    // status, `record.licensed` / `record.discipline` come DOWN — they are not
    // re-worded to survive (research file §re-verification).
    record: {
      eyebrow: 'Credentials',
      title: 'Practice record — facts you can check',
      licensed: 'Licensed since 2021-11-03 — Class L2 - RCIC, status Active',
      discipline: 'Zero disciplinary record on the CICC public register',
      focus: 'Practice focus: Tech & STEM immigration pathways',
      based: 'Based in Greater Vancouver (Burnaby), BC — consultations by video',
      languages: 'Languages: English, 中文',
      checked:
        'Licence and discipline fields as published by the CICC public register, checked 2026-07-29. Both can change at any time — check the live profile yourself:',
    },
    consultant: {
      eyebrow: 'Our consultant',
      hero: {
        title: 'Meet Your Principal Consultant',
        // [T243] The ground truth's page-head H1 + lead (site/about.html
        // §page-head), with every FACT replaced by ours: the licence year comes
        // from the register capture above, and the location is the one this site
        // already publishes everywhere else ('Greater Vancouver (Burnaby), BC' —
        // footer, contact, home). The zip's "#R708210" becomes the house form
        // `RCIC R708210`, which is what `about.verify.licence` publishes.
        headline: 'Yansi He — Founder & Principal Consultant, RCIC R708210',
        intro: 'Licensed and regulated by the College of Immigration and Citizenship Consultants (CICC) since 2021. Tech & STEM immigration specialist based in Greater Vancouver (Burnaby), BC — serving clients in English and 中文, in Canada and worldwide.',
        highlight1: {
          title: 'Licensed Expert',
          desc: 'Regulated Canadian Immigration Consultant (RCIC) ensuring full compliance.',
        },
        highlight2: {
          title: 'Strategic Approach',
          desc: 'Turns complex regulations into clear, personalized immigration roadmaps.',
        },
        highlight3: {
          title: 'Experienced Guidance',
          desc: 'Experienced guidance across a wide range of Canadian immigration cases — from skilled workers to families and businesses.',
        },
        subtitle: 'Yansi He, Licensed RCIC (R708210) | Tech & Business Immigration Specialist',
        bio1: 'With extensive experience in Canadian immigration law and a specialized focus on technology and business pathways, Yansi He provides strategic roadmaps for clients navigating complex immigration scenarios.',
        bio2: 'As a licensed Regulated Canadian Immigration Consultant (RCIC), Yansi ensures full regulatory compliance while advocating for your best interests throughout the immigration process.',
        bio3: 'Her expertise across technology and STEM pathways, Provincial Nominee Programs, and business immigration supports a wide range of professionals, families, and entrepreneurs in building their lives in Canada.',
      },
      credentials: {
        licensed: 'Licensed RCIC',
        tech: 'Tech/STEM Specialist',
        business: 'Business Immigration',
        complex: 'Complex Cases Expert',
      },
    },
    // ── [T249] 改写刀 (密度深研 §2B) — three paragraphs became three list rows ──
    // The three strings below are the same three CLAIMS, shortened to one scannable
    // line each and rendered as a v2 CheckList instead of a stack of prose (§3's
    // /about prescription: 散文段转列表). ZERO SEMANTIC DRIFT is the card's red
    // line, so the fact inventory is pinned test-side — src/pages/about/
    // u7cAboutProse.test.tsx lists every fact each string carried BEFORE the
    // rewrite (assessment · profile · most secure pathway · permanent residence /
    // every case · eligibility criteria · documentation · strategic timing ·
    // strength of your application / regulatory compliance · transparent
    // communication · every step) and fails if any of them stops rendering, in
    // either language. Filler is all that left: "We conduct a", "receives a
    // comprehensive evaluation of", "Our commitment to … ensures".
    standard: {
      title: 'Our Professional Standard',
      desc1: 'A rigorous legal assessment of your profile — to identify your most secure pathway to permanent residence.',
      desc2: 'Every case: eligibility criteria, documentation and strategic timing, evaluated in full to maximize the strength of your application.',
      desc3: 'Regulatory compliance and transparent communication — so you understand every step of your immigration journey.',
    },
    foundation: {
      title: 'Our Professional Foundation',
      subtitle: 'Built on principles we hold ourselves to at every step of your immigration journey.',
      integrity: {
        title: 'Integrity',
        desc: 'Unbiased legal advice and transparent fee structures. No hidden costs, no false promises.',
      },
      expertise: {
        title: 'Expertise',
        desc: 'Specialized knowledge in federal and provincial immigration streams, with focus on Tech/STEM and business pathways.',
      },
      dedication: {
        title: 'Dedication',
        desc: 'Commitment to your long-term success in Canada, from initial consultation to permanent residence and beyond.',
      },
    },
    intro: {
      title: 'About Think Forward Immigration Consulting',
      // [T249] The firm's name opened this paragraph AND is the heading directly
      // above it, so saying it twice was the filler; every other fact stays
      // (professional immigration consulting firm · Vancouver, British Columbia ·
      // skilled professionals · tech workers · business entrepreneurs · the
      // Canadian immigration system · confidence and clarity).
      desc: 'A professional immigration consulting firm in Vancouver, British Columbia. We help skilled professionals, tech workers and business entrepreneurs navigate the Canadian immigration system with confidence and clarity.',
    },
    // [T243] The ground truth's closing band (site/about.html §CTA BANNER):
    // the free assessment is the primary door site-wide (design-hybrid §6), the
    // paid consultation the quieter second one. I29 keeps that to ONE solid.
    cta: {
      title: 'Not sure where to start? Take the free 10-minute assessment first.',
      desc: 'No sign-up and no payment — it maps the pathways that fit your situation in about ten minutes.',
      action: 'Start your free assessment',
    },
  },

  // T28 — 问卷字段配置 admin panel. zh-primary; en gloss. Bilingual in both halves.
  admin: {
    fieldOverrides: {
      title: 'Intake field configuration',
      intro: 'Choose which questions each pathway asks — set any question to required, optional, or hidden.',
      floorNote: 'A question an active official form requires is locked to the form’s minimum: it can be made stricter but never loosened.',
      pathwayLabel: 'Pathway',
      loading: 'Loading…',
      noContract: 'No questionnaire is available for this pathway yet.',
      state: {
        required: 'Required',
        optional: 'Optional',
        hidden: 'Hidden',
      },
      lockFormRequired: 'Required by {{forms}} — cannot be made optional or hidden',
      lockFormConditional: 'Conditionally required by {{forms}} — cannot be hidden',
      lockBranch: 'A branch condition for other questions — cannot be hidden',
      branchTag: 'Branch condition',
      updatedBy: 'Updated',
      defaultTag: 'Default',
      restoreDefault: 'Restore default',
      history: 'Change history',
    },
  },

};

export default translation;
